import React from "react";
import * as Highcharts from "highcharts";
import highchartsMore from "highcharts/highcharts-more.js";
import highchartsDumbbell from "highcharts/modules/dumbbell";
import highchartsLollipop from "highcharts/modules/lollipop";
import HighchartsReact from "highcharts-react-official";
import {
  dateFormaterWithMonth,
  dateFormatWithYear,
} from "../../../utils/commonfunction";
import "./commonlollipopchart.scss";
import { pageLocale } from "../../../utils/locale";
import { getParams } from "../../../utils/helpers";

const CommonLollipopChart = ({
  graphOptions,
  historyData,
  forPDF,
  criteriaData,
  hidePlotLines = false,
  bands,
}) => {
  highchartsMore(Highcharts);
  highchartsDumbbell(Highcharts);
  highchartsLollipop(Highcharts);
  const { sit_to_standup_time, head_wobbling, attempts_to_rise } =
    pageLocale("newreport");
  const parsedData = [];
  const { locale } = getParams();
  const widthHandler = () => {
    if (locale === "ja") {
      return { width: 205, height: 170 };
    } else return { width: 205, height: 150 };
  };
  const resolution = forPDF
    ? widthHandler()
    : {
        width: window.innerWidth < 770 ? window.innerWidth - 50 : 400,
        height: "100%",
      };
  const raw = historyData?.data || historyData;
  const dataFinal = Array.isArray(raw)
    ? [...raw].sort(
        (a, b) =>
          new Date(a.timeStamp || a.timestamp).getTime() -
          new Date(b.timeStamp || b.timestamp).getTime(),
      )
    : [];
  dataFinal.forEach((element) => {
    const val = element.value ?? element.time;
    const obj = {
      name: element.timeStamp || element.timestamp,
      y: val,
    };
    parsedData.push(obj);
  });

  //handling dynamic y axis
  const yRange = () => {
    const safeNum = (v, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    let range = [];
    if (forPDF) {
      range = [historyData?.min, historyData?.max];
    } else {
      range = [
        criteriaData?.good ?? graphOptions?.green,
        criteriaData?.bad ?? graphOptions?.red,
      ];
    }
    const dataValues =
      dataFinal?.length > 0
        ? Math.max(...dataFinal.map((l) => l.value ?? l.time ?? 0))
        : 0;
    // For bands we use the highest "lower" threshold (the boundary into the
    // worst severity tier) plus headroom. Using band uppers directly inflates
    // the axis when the worst tier extends to a very large value, squishing
    // real data points to the bottom of the chart.
    const bandLow = (b) =>
      Array.isArray(b?.minMax) ? safeNum(b.minMax[0], 0) : safeNum(b?.lower, 0);
    const bandCeiling =
      Array.isArray(bands) && bands.length > 0
        ? Math.max(...bands.map(bandLow)) * 1.2
        : 0;
    const maxRange =
      Math.max(...range.map((v) => safeNum(v, 0)), dataValues, bandCeiling) *
      1.1;
    const maxValue = Math.ceil(maxRange);
    //return 1;
    return {
      min: 0,
      max: maxValue,
      labels: {
        formatter: function () {
          return Number.isInteger(this.value) || maxValue <= 1
            ? this.value
            : "";
        },
      },
    };
  };

  const greenLineHanlder = () => criteriaData?.good ?? graphOptions?.green;
  const redLineHanlder = () => criteriaData?.bad ?? graphOptions?.red;
  const tooltiphandler = (val) => {
    if (
      graphOptions?.name === sit_to_standup_time ||
      graphOptions?.chartTitle === sit_to_standup_time
    ) {
      if (val == 0) {
        return "NA";
      } else {
        return (
          Number(Math.abs(val).toFixed(2)) +
          " " +
          (graphOptions?.unit ? graphOptions?.unit : "")
        );
      }
    } else {
      return (
        Number(Math.abs(val).toFixed(2)) +
        " " +
        (graphOptions?.unit ? graphOptions?.unit : "")
      );
    }
  };

  const dataLabelHandler = (val) => {
    if (
      graphOptions?.name === sit_to_standup_time ||
      graphOptions?.chartTitle === sit_to_standup_time
    ) {
      if (val == 0) {
        return "NA";
      } else {
        return Number(Math.abs(val).toFixed(2));
      }
    } else {
      return Number(Math.abs(val).toFixed(2));
    }
  };
  const options = {
    chart: {
      type: "line",
      ...resolution,
      // height:  "100%", // important to fix min strat zero if we mention max height
      // margin: "0px",
      spacingBottom: 0,
      spacingRight: forPDF ? 8 : undefined,
      spacingLeft: forPDF ? 4 : undefined,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: graphOptions?.title,
      align: "left",
      margin: 4,
      style: {
        fontSize: "12px !important",
        color: "#919191 !important",
        fontWeight: "200",
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      useHTML: true,
      formatter: function () {
        const opt = this.point.options;
        const val =
          opt.y !== null || opt.y !== undefined
            ? opt.y.toString()
            : opt.low.toString();
        return `<div style="direction:ltr;align-items:center;"><strong style="font-size: 14px;;margin: 0;padding: 0;font-weight: 300">${dateFormatWithYear(
          opt?.name,
        )}</strong><br/><p style="text-align:center; font-weight: 600; font-size: 14px; margin: 0;padding: 0">${tooltiphandler(
          val,
        )}</p></div>`;
      },
      style: {
        fontWeight: "600",
      },
    },
    plotOptions: {
      series: {
        connectorColor: "#84A4E1",
        connectorWidth: 3,
        marker: {
          radius: forPDF ? 7 : 9,
        },
      },
    },

    xAxis: {
      type: "category",
      lineColor:
        graphOptions?.name === attempts_to_rise ||
        graphOptions?.chartTitle === attempts_to_rise
          ? "#EE4B4B"
          : "#D4D6E2",
      labels: {
        // formatter: function () {
        //   return dateFormater(this?.value, !forPDF);
        // },
        formatter: function () {
          return dateFormaterWithMonth(this?.value);
        },
        rotation: 0,
        style: {
          fontSize: forPDF ? "8px" : "12px",
          fontWeight: "600",
          fontFamily: '"Noto Sans CJK JP", sans-serif !important',
        },
      },
    },

    yAxis: [
      {
        // min: 0,
        ...yRange(),
        // tickAmount: 4,
        // tickPositioner: function () {
        //   return graphOptions?.yAxis;
        // },=
        gridLineColor: "#D8D8D8",
        title: {
          text: "",
        },
        labels: {
          padding: 0,
          margin: 0,
          x: -5,
          style: {
            fontSize: forPDF ? "8px" : "13px",
            fontWeight: "600",
            fontFamily: '"Noto Sans CJK JP", sans-serif !important',
          },
        },
        plotLines:
          Array.isArray(bands) && bands.length > 0
            ? []
            : hidePlotLines
            ? []
            : [
                {
                  color: "#38C93E",
                  value: forPDF ? historyData?.min : greenLineHanlder(),
                  width: 3,
                  dashStyle: "Solid",
                  zIndex: 1,
                },
                {
                  color: "#EE4B4B",
                  value: forPDF ? historyData?.max : redLineHanlder(),
                  width: 3,
                  dashStyle: "Solid",
                  zIndex: 1,
                },
              ],
        plotBands:
          Array.isArray(bands) && bands.length > 0
            ? (() => {
                const mapped = bands
                  .map((b) => {
                    const from = Array.isArray(b?.minMax)
                      ? Number(b.minMax[0])
                      : Number(b?.lower);
                    const to = Array.isArray(b?.minMax)
                      ? Number(b.minMax[1])
                      : Number(b?.upper);
                    return { from, to, severity: b?.severity };
                  })
                  .filter(
                    (b) => Number.isFinite(b.from) && Number.isFinite(b.to),
                  );
                // Extend the topmost band to fill the chart — the API's upper
                // bound is often finite and leaves whitespace above the last band.
                if (mapped.length > 0) {
                  const topIdx = mapped.reduce(
                    (maxI, b, i, arr) => (b.from > arr[maxI].from ? i : maxI),
                    0,
                  );
                  mapped[topIdx] = { ...mapped[topIdx], to: 99999 };
                }
                return mapped.map((b) => ({
                  from: b.from,
                  to: b.to,
                  color:
                    b.severity === "good"
                      ? "#E4F8E2"
                      : b.severity === "average"
                      ? "#FFF6DC"
                      : b.severity === "poor"
                      ? "#F8E2E2"
                      : "transparent",
                  zIndex: 0,
                }));
              })()
            : hidePlotLines
            ? []
            : [
                {
                  from: 0,
                  to: 1.5,
                  color:
                    graphOptions?.name === head_wobbling ||
                    graphOptions?.chartTitle === head_wobbling
                      ? "#E4F8E2"
                      : "transparent",
                },
                {
                  from: 1.5,
                  to: 15,
                  color:
                    graphOptions?.name === head_wobbling ||
                    graphOptions?.chartTitle === head_wobbling
                      ? "#F8E2E2"
                      : "transparent",
                },
              ],
      },
    ],

    series: [
      {
        color: "#ADC7EE",
        data: parsedData, //
        dataLabels: {
          enabled: true,
          useHTML: true,
          align: "left",
          //horizontalAlign: "center",
          // verticalAlign: "middle",
          //crop: false,
          // overflow: "allow",
          // alignTo: "plotEdges",
          x: -10,
          y: -5,
          fontSize: "8px",
          formatter: function () {
            return (
              forPDF &&
              `<div style="color: #434343; background-color: #f2f2f2; font-size: 8px;padding: 0px">${dataLabelHandler(
                this?.point?.options?.y,
              )}</div>`
            );
          },
        },
        marker: {
          enabled: true,
          radius: 5,
          fillColor: "#3A78D5", // Marker color similar to the image
        },
      },
      // {
      //   color: "#84A4E1",
      //   type: "line",
      //   dashStyle: "solid",
      //   data: parsedData, //
      //   enableMouseTracking: false,
      //   linkedTo: ":previous",
      //   showInLegend: false,
      //   pointPadding: 0.3,
      //   pointWidth: 40,
      // },
    ],
  };
  return (
    <div
      style={forPDF ? {} : { maxHeight: "600px" }}
      className="lollipop-chart-wrapper"
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default CommonLollipopChart;
